import React, { useMemo, useState } from 'react';
import { useAtom } from 'jotai/index';
import styles from './css/index.module.scss';
import useTranslations from '../../hooks/useTranslations';
import { DICTIONARY } from '../../translations/dictionary';
import TutorialBackground from './components/TutorialBackground';
import {
  configurationDetailsWrapperRefAtom,
  scrollableContentRefAtom,
  windowHeightAtom,
  windowWidthAtom,
} from '../../atoms/globalAtoms';
import TutorialModal from './components/TutorialModal';
import { isTutorialActiveAtom, tutorialStageAtom, tutorialStepAtom } from '../../atoms/tutorialsAtoms';
import useScreenPosition from '../../hooks/useScreenPosition';
import useTutorials from '../../hooks/useTutorials';

const DEFAULT_SCREEN_WIDTH = 1270;
const NAV_BAR_WIDTH = 353;

function Tutorials() {
  const { t, lang } = useTranslations();
  const { finishStage1, closeTutorials } = useTutorials();

  const [windowWidth] = useAtom(windowWidthAtom);
  const [windowHeight] = useAtom(windowHeightAtom);
  const [tutorialStep, setTutorialStep] = useAtom(tutorialStepAtom);
  const [tutorialStage, setTutorialStage] = useAtom(tutorialStageAtom);
  const [isTutorialActive, setIsTutorialActive] = useAtom(isTutorialActiveAtom);
  const [configurationDetailsWrapperRef] = useAtom(configurationDetailsWrapperRefAtom);
  const [scrollableContentRef] = useAtom(scrollableContentRefAtom);

  const configurationDetailsSizes = useScreenPosition(configurationDetailsWrapperRef);

  const stage1Steps = [
    {
      countXFromNavBar: true,
      countXScreenRelative: true,
      background: {
        x: 514, y: 180, width: 595, height: 275, isInteractive: false,
      },
      modal: {
        x: windowHeight <= 700 ? 120 : 513, y: windowHeight <= 700 ? 180 : 490, text: t(DICTIONARY.tutorials.stage1.step1), arrowPosition: windowHeight <= 700 ? 'right' : 'top', arrowX: 35, arrowY: 35, withButtons: true,
      },
    }, {
      countXFromNavBar: false,
      countXScreenRelative: false,
      background: {
        x: 13, y: 75, width: 323, height: 400, isInteractive: false,
      },
      modal: {
        x: 390, y: 75, text: t(DICTIONARY.tutorials.stage1.step2), arrowPosition: 'left', arrowY: 35, withButtons: true,
      },
    }, {
      countXFromNavBar: true,
      countXScreenRelative: true,
      background: {
        x: lang === 'ru' ? 420 : 450, y: 440, width: 369, height: 110, isInteractive: false,
      },
      modal: {
        x: lang === 'ru' ? 420 : 450, y: 220, text: t(DICTIONARY.tutorials.stage1.step3), arrowPosition: 'bottom', arrowX: 35, withButtons: true,
      },
    }, {
      countXFromNavBar: false,
      countXScreenRelative: false,
      background: {
        x: windowWidth / 2, y: windowHeight / 2, width: 0, height: 0, isInteractive: false,
      },
      modal: {
        x: (windowWidth - 369) / 2, y: (windowHeight - 183) / 2, text: t(DICTIONARY.tutorials.stage1.step4), withButtons: true,
      },
    }];

  const stage2Steps = [
    {
      countXFromNavBar: true,
      countXScreenRelative: true,
      background: {
        x: windowHeight <= 841 ? 885 : 890, y: 264, width: 164, height: 175, isInteractive: true,
      },
      modal: {
        x: windowHeight <= 841 ? 680 : 685, y: 460, text: t(DICTIONARY.tutorials.stage2.step1), arrowPosition: 'top', arrowX: 320,
      },
    }, {
      countXFromNavBar: false,
      countXScreenRelative: false,
      background: {
        x: 13, y: 199, width: 323, height: 45, isInteractive: true,
      },
      modal: {
        x: 360, y: 199, text: t(DICTIONARY.tutorials.stage2.step3), arrowPosition: 'left', arrowY: 18,
      },
    }, {
      countXFromNavBar: false,
      countXScreenRelative: false,
      background: {
        x: 15, y: 328, width: 323, height: 45, isInteractive: true,
      },
      modal: {
        x: 360, y: 328, text: t(DICTIONARY.tutorials.stage2.step4), arrowPosition: 'left', arrowY: 18,
      },
    }, {
      countXFromNavBar: true,
      countXScreenRelative: true,
      background: {
        x: windowHeight <= 841 ? 655 : 659, y: 164, width: 80, height: 386, isInteractive: true,
      },
      modal: {
        x: windowHeight <= 841 ? 271 : 275, y: 164, text: t(DICTIONARY.tutorials.stage2.step5), arrowPosition: 'right', arrowY: 35,
      },
    }, {
      countXFromNavBar: false,
      countXScreenRelative: false,
      background: {
        x: (windowWidth - 260) / 2, y: (windowHeight - 50) / 2, width: 260, height: 84, isInteractive: true,
      },
      modal: {
        y: -200,
      },
    }, {
      countXFromNavBar: false,
      countXScreenRelative: false,
      background: {
        x: (windowWidth - 230) / 2, y: 177 + (windowHeight - 50) / 2, width: 230, height: 52, isInteractive: true,
      },
      modal: {
        x: 150 + windowWidth / 2, y: -200,
      },
    }, {
      countXFromNavBar: false,
      countXScreenRelative: false,
      background: {
        x: (windowWidth - 260) / 2, y: 80 + (windowHeight - 50) / 2, width: 260, height: 55, isInteractive: true,
      },
      modal: {
        x: 150 + windowWidth / 2, y: 80 + (windowHeight - 50) / 2, text: t(DICTIONARY.tutorials.stage2.step8), arrowPosition: 'left', arrowY: 24,
      },
    }, {
      countXFromNavBar: true,
      countXScreenRelative: true,
      background: {
        x: windowHeight <= 841 ? 872 : 876, y: 550, width: 143, height: 56, isInteractive: true,
      },
      modal: {
        x: windowHeight <= 841 ? 486 : 490, y: windowHeight <= 700 ? 467 : 550, text: t(DICTIONARY.tutorials.stage2.step9), arrowPosition: 'right', arrowY: windowHeight <= 700 ? 100 : 24,
      },
    }, {
      countXFromNavBar: false,
      countXScreenRelative: false,
      background: {
        x: configurationDetailsSizes.left + (lang === 'ru' ? 188 : 176), y: 236, width: (lang === 'ru' ? 176 : 150), height: 48, isInteractive: true,
      },
      modal: {
        x: configurationDetailsSizes.left + (lang === 'ru' ? 188 : 176), y: 300, text: t(DICTIONARY.tutorials.stage2.step10), arrowPosition: 'top', arrowX: 35,
      },
    }, {
      countXFromNavBar: false,
      countXScreenRelative: false,
      background: {
        x: configurationDetailsSizes.left + (lang === 'ru' ? 543 : 516), y: 236, width: (lang === 'ru' ? 201 : 166), height: 48, isInteractive: true,
      },
      modal: {
        x: configurationDetailsSizes.left + (lang === 'ru' ? 543 : 516), y: 300, text: t(DICTIONARY.tutorials.stage2.step11), arrowPosition: 'top', arrowX: 35,
      },
    }, {
      countXFromNavBar: false,
      countXScreenRelative: false,
      background: {
        x: configurationDetailsSizes.left - 5, y: 236, width: (lang === 'ru' ? 192 : 182), height: 48, isInteractive: true,
      },
      modal: {
        x: configurationDetailsSizes.left - 5, y: 300, text: t(DICTIONARY.tutorials.stage2.step12), arrowPosition: 'top', arrowX: 35,
      },
    },
  ];

  const getX = ({ x, countXFromNavBar, countXScreenRelative }) => {
    if (!countXScreenRelative) return x;

    let newX = (x / DEFAULT_SCREEN_WIDTH) * windowWidth;

    if (countXFromNavBar) {
      const dif = windowWidth - DEFAULT_SCREEN_WIDTH;
      newX = x + dif / 2;
    }

    return Math.ceil(newX);
  };

  const getBackgroundSizes = (step) => ({
    ...step,
    background: { ...step.background, x: getX({ x: step.background.x, countXFromNavBar: step.countXFromNavBar, countXScreenRelative: step.countXScreenRelative }) },
    modal: { ...step.modal, x: getX({ x: step.modal.x, countXFromNavBar: step.countXFromNavBar, countXScreenRelative: step.countXScreenRelative }) },
  });

  const stepsToDraw = useMemo(() => (tutorialStage === 1 ? stage1Steps : stage2Steps).map(getBackgroundSizes), [stage1Steps, stage2Steps, tutorialStage]);

  if (!isTutorialActive || windowWidth <= 900) return null;

  return (
    <div className={styles.tutorialWrapper}>
      <TutorialBackground
        {...stepsToDraw[tutorialStep - 1].background}
      />
      <TutorialModal
        {...stepsToDraw[tutorialStep - 1].modal}
        tutorialStep={tutorialStep}
        setTutorialStep={setTutorialStep}
        setTutorialStage={setTutorialStage}
        totalSteps={tutorialStage === 1 ? stage1Steps.length : stage2Steps.length}
        setIsTutorialActive={setIsTutorialActive}
        finishStage1={finishStage1}
        closeTutorials={closeTutorials}
      />
    </div>
  );
}

export default Tutorials;
